/* eslint-disable */
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import omit from 'lodash/omit';
import { useCallback, useContext, useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';

import { ApiError, PeerRating, PeerRatingsService, ProviderService } from '../api';
import { FormSelectField } from '../app/components/FormSelectField';
import { FormStarRatingField } from '../app/components/FormStarRatingField';
import { FormTextareaField } from '../app/components/FormTextareaField';
import { FormTextField } from '../app/components/FormTextField';
import { ServerErrorsAlert } from '../app/components/ServerErrorsAlert';
import { FormSubmitContext } from '../app/contexts/FormSubmitContext';
import { useDocumentTitle } from '../app/hooks/use-document-title';
import { providerQueryKeys } from '../app/query-keys-factories/providers';
import { handleApiError } from '../app/utils/errorhandling';
import { clearBrowserCookies } from '../utils/cookies';
import { generateShowFieldErrors, generateZEnumFromEnum } from '../utils/forms';
import { NotFound } from './NotFound';

const validationSchema = z.object({
  responder_name: z.string().min(1, { message: 'First Name is required' }),
  responder_email: z.string().min(1, { message: 'Email is required' }).email({
    message: "That email doesn't look quite right.",
  }),
  responder_npi: z.string().min(1, { message: 'NPI is required' }),
  clinical_competency_rating: z.coerce
    .number()
    .min(1, { message: 'must be at least 1' })
    .max(5, { message: 'must be up to 5' }),
  communication_and_teamwork_rating: z.coerce
    .number()
    .min(1, { message: 'must be at least 1' })
    .max(5, { message: 'must be up to 5' }),
  patient_closecase_rating: z.coerce
    .number()
    .min(1, { message: 'must be at least 1' })
    .max(5, { message: 'must be up to 5' }),
  general_rating: z.coerce
    .number()
    .min(1, { message: 'must be at least 1' })
    .max(5, { message: 'must be up to 5' }),
  comment: z.string().optional(),
  responder_relationship: generateZEnumFromEnum(window.PEER_RELATIONSHIP_TYPES),
});

export const PeerRatingPage = () => {
  useDocumentTitle('Peer Rating');
  const { isSubmitEnabled } = useContext(FormSubmitContext);
  const { providerId } = useParams<{ providerId: string }>();
  const navigate = useNavigate();
  const {
    handleSubmit,
    formState: { errors },
    register,
    clearErrors,
    control,
  } = useForm({
    resolver: zodResolver(validationSchema),
  });

  const {
    isLoading: isProviderLoading,
    error: providerFetchError,
    data: provider,
  } = useQuery({
    queryKey: providerQueryKeys.publicDetail(providerId!),
    queryFn: async () =>
      await ProviderService.providerPublicRetrieve({
        providerId: providerId!,
      }),
  });

  const [serverErrors, setServerErrors] = useState<ApiError>();
  const [showGlobalServerErrors, setShowGlobalServerErrors] = useState(false);
  const [showServerFieldErrors, setShowServerFieldErrors] = useState<{
    responder_name?: boolean;
    responder_email?: boolean;
    responder_npi?: boolean;
    clinical_competency_rating?: boolean;
    communication_and_teamwork_rating?: boolean;
    patient_closecase_rating?: boolean;
    general_rating?: boolean;
    comment?: boolean;
    recaptcha?: boolean;
    provider?: boolean;
    responder_relationship?: boolean;
  }>({});

  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleReCaptchaVerify = useCallback(async () => {
    if (!executeRecaptcha) {
      console.log('Execute recaptcha not yet available');
      return;
    }

    return await executeRecaptcha('rate');
  }, [executeRecaptcha]);

  useEffect(() => {
    handleReCaptchaVerify();
  }, [handleReCaptchaVerify]);

  // Callback for clearing alerts received from server
  function handleClearErrorsAlert() {
    setShowGlobalServerErrors(false);
  }

  const handleSubmitClick = () => {
    clearErrors();
    setShowGlobalServerErrors(false);
    setShowServerFieldErrors({});
  };

  const mutationPeerRating = useMutation({
    mutationFn: async (data: PeerRating) => {
      clearBrowserCookies();
      localStorage.removeItem('session');
      await PeerRatingsService.peerRatingsCreate({ requestBody: data });
    },
  });

  // Callback for registration form submission
  const onSubmit = async (data) => {
    try {
      await mutationPeerRating.mutateAsync({
        ...data,
        provider: providerId,
        recaptcha: await handleReCaptchaVerify(),
      } as PeerRating);
      navigate('/app/rating-thank-you');
    } catch (errors) {
      if (errors.status === 400) {
        const fieldErrors = omit(errors.body, ['non_field_errors', 'uid', 'token']);
        setServerErrors(errors);
        setShowServerFieldErrors(generateShowFieldErrors(fieldErrors));
        setShowGlobalServerErrors(true);
      }
      handleApiError(errors);
    }
  };

  if (!provider && !isProviderLoading && providerFetchError) {
    return <NotFound />;
  }

  return (
    <Container className="px-4 py-5" fluid>
      <Container>
        <Row className="d-flex justify-content-center align-items-center">
          <Col className="mb-5" lg={8} md={12}>
            {!isProviderLoading && (
              <h1 className="fw-bold fs-1 text-uppercase text-center text-primary mt-5">
                Rate provider {provider?.first_name} {provider?.last_name}
              </h1>
            )}
            <p>
              Your answers below are anonymous. Your personal information is only used to validate
              your identity as reviews must be completed by healthcare professionals.
            </p>
            <Row className="d-flex justify-content-center align-items-center mb-3">
              <Col lg={10} sm={11}>
                <Form noValidate onSubmit={handleSubmit(onSubmit)} id="register-form">
                  <FormTextField
                    label="Your Name"
                    name="responder_name"
                    placeholder="Enter your name"
                    errors={errors}
                    showServerFieldErrors={showServerFieldErrors}
                    setShowServerFieldErrors={setShowServerFieldErrors}
                    register={register}
                    required
                  />
                  <FormTextField
                    label="Your Email"
                    name="responder_email"
                    placeholder="Enter your email"
                    errors={errors}
                    showServerFieldErrors={showServerFieldErrors}
                    setShowServerFieldErrors={setShowServerFieldErrors}
                    register={register}
                    required
                  />
                  <FormTextField
                    label="Your NPI"
                    name="responder_npi"
                    placeholder="Enter your NPI"
                    errors={errors}
                    showServerFieldErrors={showServerFieldErrors}
                    setShowServerFieldErrors={setShowServerFieldErrors}
                    register={register}
                    required
                  />
                  <FormSelectField
                    label="Your Relationship to Provider"
                    name="responder_relationship"
                    placeholder="Select your relationship"
                    options={window.PEER_RELATIONSHIP_TYPES}
                    errors={errors}
                    showServerFieldErrors={showServerFieldErrors}
                    setShowServerFieldErrors={setShowServerFieldErrors}
                    register={register}
                    required
                  />
                  <p>
                    <strong>
                      On a scale of 1 to 5, with 1 being poor and 5 being excellent, how would you
                      rate the provider above? Copy
                    </strong>
                  </p>
                  <FormStarRatingField
                    label="Clinical Competency"
                    name="clinical_competency_rating"
                    errors={errors}
                    showServerFieldErrors={showServerFieldErrors}
                    setShowServerFieldErrors={setShowServerFieldErrors}
                    control={control}
                    required
                  />
                  <FormStarRatingField
                    label="Communication and Teamwork"
                    name="communication_and_teamwork_rating"
                    errors={errors}
                    showServerFieldErrors={showServerFieldErrors}
                    setShowServerFieldErrors={setShowServerFieldErrors}
                    control={control}
                    required
                  />
                  <FormStarRatingField
                    label="Ability to manage patient load"
                    name="patient_closecase_rating"
                    errors={errors}
                    showServerFieldErrors={showServerFieldErrors}
                    setShowServerFieldErrors={setShowServerFieldErrors}
                    control={control}
                    required
                  />
                  <FormStarRatingField
                    label="Overall provider rating"
                    name="general_rating"
                    errors={errors}
                    showServerFieldErrors={showServerFieldErrors}
                    setShowServerFieldErrors={setShowServerFieldErrors}
                    control={control}
                    required
                  />
                  <FormTextareaField
                    label="Please provide any additional comments"
                    name="comment"
                    placeholder="Enter your comment"
                    errors={errors}
                    showServerFieldErrors={showServerFieldErrors}
                    setShowServerFieldErrors={setShowServerFieldErrors}
                    register={register}
                  />
                  <div className="d-grid py-3 mt-5">
                    <Button
                      className="fs-4 py-3 rounded-0 btn-drop-shadow"
                      type="submit"
                      variant="primary"
                      onClick={() => handleSubmitClick()}
                      disabled={mutationPeerRating.isLoading || !isSubmitEnabled}
                    >
                      Submit
                    </Button>
                  </div>
                </Form>
                <ServerErrorsAlert
                  errors={serverErrors}
                  showErrors={showGlobalServerErrors}
                  clearErrors={handleClearErrorsAlert}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>
    </Container>
  );
};
